import React, { useEffect, useState } from 'react';
import { employeesAPI } from '../services/api';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Avatar, AvatarFallback } from './ui/avatar';
import { Badge } from './ui/badge';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from './ui/dialog';
import { toast } from 'sonner';
import { UserCog, Search, X, Loader2, Check } from 'lucide-react';

/**
 * Reporting Manager card — visible to HR / super_admin on the Employee Detail page.
 * Lets them search and assign (or clear) the direct manager. Powers WFH/Leave/Expense
 * approval workflows that resolve `approver_type = manager`.
 */
const ReportingManagerCard = ({ employee, canManage, onChanged }) => {
  const [manager, setManager] = useState(null);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [candidates, setCandidates] = useState([]);
  const [searching, setSearching] = useState(false);
  const [saving, setSaving] = useState(false);

  const fetchManager = async () => {
    if (!employee?.manager_id) { setManager(null); return; }
    setLoading(true);
    try {
      const r = await employeesAPI.getById(employee.manager_id);
      setManager(r.data);
    } catch { setManager(null); }
    finally { setLoading(false); }
  };
  useEffect(() => { fetchManager(); /* eslint-disable-next-line */ }, [employee?.manager_id]);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      setSearching(true);
      try {
        const r = await employeesAPI.getAll(employee?.company_id ? { company_id: employee.company_id } : {});
        if (cancelled) return;
        const q = query.trim().toLowerCase();
        const list = (r.data || []).filter((e) => e.id !== employee.id);
        const filtered = q
          ? list.filter((e) =>
              (e.first_name || '').toLowerCase().includes(q) ||
              (e.last_name || '').toLowerCase().includes(q) ||
              (e.email || '').toLowerCase().includes(q) ||
              (e.employee_code || '').toLowerCase().includes(q) ||
              (e.designation || '').toLowerCase().includes(q))
          : list;
        setCandidates(filtered.slice(0, 30));
      } finally { setSearching(false); }
    })();
    return () => { cancelled = true; };
  }, [open, query, employee?.id, employee?.company_id]);

  const assign = async (candidate) => {
    setSaving(true);
    try {
      await employeesAPI.assignManager(employee.id, candidate ? candidate.id : null);
      toast.success(candidate ? `Manager set to ${candidate.first_name} ${candidate.last_name}` : 'Manager cleared');
      setOpen(false);
      if (onChanged) await onChanged();
      // Optimistic — trigger a refresh via the employee prop change; also refetch manager doc
      if (candidate) setManager(candidate); else setManager(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed');
    } finally { setSaving(false); }
  };

  const initials = (fn, ln) => `${(fn || '')[0] || ''}${(ln || '')[0] || ''}`.toUpperCase();

  if (!canManage) return null;

  return (
    <>
      <Card data-testid="reporting-manager-card">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center gap-2"><UserCog size={18} />Reporting Manager</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-sm text-muted-foreground flex items-center gap-2"><Loader2 size={14} className="animate-spin" />Loading…</div>
          ) : manager ? (
            <div className="flex items-center gap-3 flex-wrap">
              <Avatar className="h-10 w-10"><AvatarFallback className="bg-primary/10 text-primary">{initials(manager.first_name, manager.last_name)}</AvatarFallback></Avatar>
              <div className="flex-1 min-w-0">
                <div className="font-medium truncate" data-testid="rm-name">{manager.first_name} {manager.last_name}</div>
                <div className="text-xs text-muted-foreground truncate">{manager.designation || '—'} · {manager.employee_code}</div>
                <div className="text-xs text-muted-foreground truncate">{manager.email}</div>
              </div>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={() => { setQuery(''); setOpen(true); }} data-testid="rm-change-btn">Change</Button>
                <Button size="sm" variant="ghost" onClick={() => assign(null)} disabled={saving} data-testid="rm-clear-btn">
                  {saving ? <Loader2 size={14} className="animate-spin" /> : <X size={14} className="text-red-600" />}
                </Button>
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div className="text-sm">
                <Badge variant="secondary" data-testid="rm-none-badge">No manager assigned</Badge>
                <p className="text-xs text-muted-foreground mt-1">Required for WFH / leave / expense manager-approval workflows.</p>
              </div>
              <Button onClick={() => { setQuery(''); setOpen(true); }} data-testid="rm-assign-btn"><UserCog size={14} className="mr-1" />Assign Manager</Button>
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg" data-testid="rm-dialog">
          <DialogHeader>
            <DialogTitle>Select a reporting manager</DialogTitle>
            <DialogDescription>Search within {employee?.first_name}&apos;s company.</DialogDescription>
          </DialogHeader>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={16} />
            <Input autoFocus placeholder="Name, email, code, designation…" className="pl-9" value={query} onChange={(e) => setQuery(e.target.value)} data-testid="rm-search" />
          </div>
          <div className="max-h-80 overflow-y-auto divide-y border rounded" data-testid="rm-candidates">
            {searching ? (
              <div className="p-4 text-sm text-muted-foreground flex items-center gap-2"><Loader2 size={14} className="animate-spin" />Searching…</div>
            ) : candidates.length === 0 ? (
              <div className="p-4 text-sm text-muted-foreground text-center">No matches.</div>
            ) : (
              candidates.map((c) => (
                <button
                  key={c.id}
                  onClick={() => assign(c)}
                  disabled={saving}
                  className="w-full text-left p-3 hover:bg-muted/30 transition flex items-center gap-3"
                  data-testid={`rm-pick-${c.employee_code}`}
                >
                  <Avatar className="h-8 w-8"><AvatarFallback className="bg-primary/10 text-primary text-xs">{initials(c.first_name, c.last_name)}</AvatarFallback></Avatar>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium truncate">{c.first_name} {c.last_name}</div>
                    <div className="text-xs text-muted-foreground truncate">{c.designation || '—'} · {c.employee_code} · {c.email}</div>
                  </div>
                  {employee?.manager_id === c.id && <Check size={16} className="text-green-600" />}
                </button>
              ))
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ReportingManagerCard;
